import React, { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { TranslationRecoveryTab } from '@/components/translations/TranslationRecoveryTab';
import { authFetch } from '@/utils/auth-fetch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, RefreshCw, Play, Globe } from 'lucide-react';

const LANGUAGES = [
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'el', name: 'Greek', flag: '🇬🇷' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
  { code: 'ca', name: 'Catalan', flag: '🏴' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  stale: 'bg-amber-100 text-amber-700',
};

function getLang(code: string) {
  return LANGUAGES.find(l => l.code === code);
}

export default function TranslationsDashboard() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const { toast } = useToast();

  if (!selectedSite) {
    return (
      <Card className="m-6">
        <CardContent className="py-10 text-center text-muted-foreground">
          Please select a site from the header to manage translations.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Globe className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Translations</h1>
      </div>
      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config">Language Configuration</TabsTrigger>
          <TabsTrigger value="coverage">Translation Coverage</TabsTrigger>
          <TabsTrigger value="browse">Product Translations</TabsTrigger>
          {user?.role === 'super_admin' && <TabsTrigger value="recovery">Recovery</TabsTrigger>}
        </TabsList>
        <TabsContent value="config">
          <LanguageConfigTab siteId={selectedSite.id} toast={toast} />
        </TabsContent>
        <TabsContent value="coverage">
          <CoverageTab siteId={selectedSite.id} toast={toast} />
        </TabsContent>
        <TabsContent value="browse">
          <BrowseTab siteId={selectedSite.id} />
        </TabsContent>
        {user?.role === 'super_admin' && <TabsContent value="recovery">
          <TranslationRecoveryTab key={selectedSite.id} siteId={selectedSite.id} />
        </TabsContent>}
      </Tabs>
    </div>
  );
}

/* ─── Tab 1: Language Configuration ─── */

function LanguageConfigTab({ siteId, toast }: { siteId: string; toast: any }) {
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/sites/${siteId}/translation-config`);
      const data = await res.json();
      setSourceLanguage(data.source_language || 'en');
      setTargetLanguages(data.target_languages || []);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [siteId, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleTarget = (code: string) => {
    setTargetLanguages(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/sites/${siteId}/translation-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_language: sourceLanguage, target_languages: targetLanguages }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Save failed');
      }
      toast({ title: 'Saved', description: 'Translation configuration updated.' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const availableTargets = LANGUAGES.filter(l => l.code !== sourceLanguage);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Language Configuration</CardTitle>
        <CardDescription>Set the source language and choose which languages to translate into.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">Source Language</label>
          <Select value={sourceLanguage} onValueChange={(v) => {
            setSourceLanguage(v);
            setTargetLanguages(prev => prev.filter(c => c !== v));
          }}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map(l => (
                <SelectItem key={l.code} value={l.code}>{l.flag} {l.name} ({l.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Target Languages</label>
          <div className="grid grid-cols-3 gap-3">
            {availableTargets.map(l => (
              <label key={l.code} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted">
                <Checkbox
                  checked={targetLanguages.includes(l.code)}
                  onCheckedChange={() => toggleTarget(l.code)}
                />
                <span>{l.flag} {l.name}</span>
              </label>
            ))}
          </div>
        </div>

        <Button onClick={save} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Configuration
        </Button>
      </CardContent>
    </Card>
  );
}

/* ─── Tab 2: Translation Coverage ─── */

interface CoverageRow {
  language: string;
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  stale: number;
}

function CoverageTab({ siteId, toast }: { siteId: string; toast: any }) {
  const [coverage, setCoverage] = useState<CoverageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState<string | null>(null);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [polling, setPolling] = useState(false);

  const loadCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/aeo-translations/coverage/${siteId}`);
      const data = await res.json();
      setCoverage(data.coverage || []);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [siteId, toast]);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  const translateLanguage = async (lang: string) => {
    setTranslating(lang);
    try {
      const res = await authFetch(`/api/aeo-translations/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, targetLanguage: lang }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Batch failed');
      toast({ title: 'Batch started', description: `Translation batch for ${getLang(lang)?.name || lang} created.` });
      loadCoverage();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setTranslating(null);
    }
  };

  const translateAll = async () => {
    setTranslatingAll(true);
    try {
      for (const row of coverage) {
        await authFetch(`/api/aeo-translations/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ siteId, targetLanguage: row.language }),
        });
      }
      toast({ title: 'All batches started', description: `Translation batches created for ${coverage.length} languages.` });
      loadCoverage();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setTranslatingAll(false);
    }
  };

  const pollBatches = async () => {
    setPolling(true);
    try {
      await authFetch(`/api/aeo-translations/poll`, { method: 'POST' });
      toast({ title: 'Poll complete', description: 'Active batches polled for updates.' });
      loadCoverage();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setPolling(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Translation Coverage</CardTitle>
            <CardDescription>Status of translations per target language.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={pollBatches} disabled={polling}>
              {polling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Poll Batches
            </Button>
            <Button size="sm" onClick={translateAll} disabled={translatingAll || coverage.length === 0}>
              {translatingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Translate All
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {coverage.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No target languages configured. Set them in the Language Configuration tab.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Language</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">Processing</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Stale</TableHead>
                <TableHead className="w-40">Progress</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coverage.map(row => {
                const lang = getLang(row.language);
                const pct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0;
                return (
                  <TableRow key={row.language}>
                    <TableCell className="font-medium">{lang?.flag} {lang?.name || row.language}</TableCell>
                    <TableCell className="text-right">{row.total}</TableCell>
                    <TableCell className="text-right">{row.pending}</TableCell>
                    <TableCell className="text-right">{row.processing}</TableCell>
                    <TableCell className="text-right">{row.completed}</TableCell>
                    <TableCell className="text-right">{row.failed}</TableCell>
                    <TableCell className="text-right">{row.stale}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={pct} className="h-2" />
                        <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => translateLanguage(row.language)}
                        disabled={translating === row.language}
                      >
                        {translating === row.language ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Tab 3: Product Translations ─── */

interface TranslationRow {
  id: string;
  source_id: string;
  status: string;
  translated_at: string | null;
  source_content_version: number;
  product_name: string | null;
  page_url: string | null;
  product_id: string | null;
}

function BrowseTab({ siteId }: { siteId: string }) {
  const { toast } = useToast();
  const [config, setConfig] = useState<{ target_languages: string[] }>({ target_languages: [] });
  const [language, setLanguage] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [translations, setTranslations] = useState<TranslationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailContent, setDetailContent] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 25;

  // Load config to get target languages
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`/api/sites/${siteId}/translation-config`);
        const data = await res.json();
        setConfig({ target_languages: data.target_languages || [] });
        if (data.target_languages?.length > 0 && !language) {
          setLanguage(data.target_languages[0]);
        }
      } catch {}
    })();
  }, [siteId]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [language, status, debouncedSearch]);

  // Load translations
  const loadTranslations = useCallback(async () => {
    if (!language) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ siteId, language, page: String(page), limit: String(limit) });
      if (status !== 'all') params.set('status', status);
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await authFetch(`/api/aeo-translations/list?${params}`);
      const data = await res.json();
      setTranslations(data.translations || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [siteId, language, status, debouncedSearch, page, toast]);

  useEffect(() => { loadTranslations(); }, [loadTranslations]);

  // Load detail
  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetailContent(null);
    try {
      const res = await authFetch(`/api/aeo-translations/${id}/content`);
      const data = await res.json();
      setDetailContent(data);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Translations</CardTitle>
        <CardDescription>Browse translated product content by language.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {config.target_languages.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No target languages configured.</p>
        ) : (
          <>
            <div className="flex gap-3 items-center flex-wrap">
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {config.target_languages.map(code => {
                    const l = getLang(code);
                    return <SelectItem key={code} value={code}>{l?.flag} {l?.name || code}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="stale">Stale</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Search products..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-64"
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : translations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No translations found.</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Translated At</TableHead>
                      <TableHead>Version</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {translations.map(t => (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(t.id)}>
                        <TableCell className="font-medium max-w-md truncate">
                          {t.product_name || t.page_url || t.source_id}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[t.status] || ''} variant="secondary">
                            {t.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {t.translated_at ? new Date(t.translated_at).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>{t.source_content_version || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-muted-foreground">
                    {total} total &middot; Page {page} of {totalPages || 1}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <Dialog open={!!selectedId} onOpenChange={(open) => { if (!open) setSelectedId(null); }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Translation Content</DialogTitle>
            </DialogHeader>
            {detailLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : detailContent ? (
              <div className="space-y-3">
                <div className="flex gap-2 items-center">
                  <Badge className={STATUS_COLORS[detailContent.status] || ''} variant="secondary">
                    {detailContent.status}
                  </Badge>
                  {detailContent.translated_at && (
                    <span className="text-sm text-muted-foreground">
                      Translated: {new Date(detailContent.translated_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <pre className="bg-muted p-4 rounded text-xs overflow-auto max-h-[50vh] whitespace-pre-wrap">
                  {JSON.stringify(detailContent.translated_content, null, 2)}
                </pre>
              </div>
            ) : (
              <p className="text-muted-foreground">No content available.</p>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
